import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/clients';
import * as session from '../api/session';
import {
  ChangePasswordCommand,
  ICurrentUserDto,
  LoginCommand,
  RefreshRequest,
  RegisterUserCommand
} from '../web-api-client';

interface AuthContextValue {
  user: ICurrentUserDto | null;
  /** Chưa biết người dùng là ai cho tới khi kiểm tra xong phiên đã lưu. */
  initialising: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** Múi giờ của trình duyệt, để máy chủ quy đổi "thời lượng" thành mốc giờ đúng với người dùng. */
function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<ICurrentUserDto | null>(() => session.load()?.user ?? null);
  const [initialising, setInitialising] = useState(() => session.load() !== null);

  // Phiên có thể bị xóa từ nơi khác: lời gọi làm mới thất bại, hoặc người dùng đăng xuất ở tab khác.
  useEffect(() => session.subscribe(active => setUser(active?.user ?? null)), []);

  // Phiên lưu trong máy chỉ là phỏng đoán. Hỏi lại máy chủ một lần lúc khởi động để biết chắc token
  // còn giá trị và hồ sơ chưa đổi (ví dụ quản trị viên vừa khóa tài khoản này).
  useEffect(() => {
    if (!session.load()) return;

    let cancelled = false;

    authApi
      .me()
      .then(profile => {
        if (cancelled) return;
        session.updateUser(profile);
        setUser(profile);
      })
      .catch(() => {
        if (!cancelled) session.clear();
      })
      .finally(() => {
        if (!cancelled) setInitialising(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await authApi.login(new LoginCommand({ email, password }));
    session.save(session.fromAuthResult(result));
  }, []);

  const register = useCallback(async (email: string, password: string, displayName: string) => {
    const result = await authApi.register(
      new RegisterUserCommand({ email, password, displayName, timeZoneId: browserTimeZone() })
    );
    session.save(session.fromAuthResult(result));
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await authApi.changePassword(new ChangePasswordCommand({ currentPassword, newPassword }));

    // Đổi mật khẩu làm máy chủ hủy mọi phiên cũ, kể cả phiên đang dùng. Đăng nhập lại bằng mật khẩu
    // mới ngay tại đây để người dùng không bị đá ra giữa chừng.
    const email = session.load()?.user?.email;

    if (email) {
      const result = await authApi.login(new LoginCommand({ email, password: newPassword }));
      session.save(session.fromAuthResult(result));
    } else {
      session.clear();
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = session.load()?.refreshToken;

    // Xóa phiên trước rồi mới báo máy chủ: dù mạng có hỏng thì trên máy này người dùng vẫn đã ra
    // khỏi tài khoản.
    session.clear();

    if (refreshToken) {
      try {
        await authApi.logout(new RefreshRequest({ refreshToken }));
      } catch {
        /* token sẽ tự hết hạn */
      }
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, initialising, login, register, changePassword, logout }),
    [user, initialising, login, register, changePassword, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth phải được dùng bên trong AuthProvider.');
  }

  return context;
}
